// @flow
import React from 'react';
import ReactHTMLParser from 'react-html-parser';

import { BasisProvider, defaultTheme, Form, Select, Input, Text } from 'basis';
import { BREAKPOINT, COLOR } from '@latitude/core/utils/constants';
import { FramedContainer } from '@latitude/framed-container';
import 'styled-components/macro';

const currency = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD'
});

export const InstalmentInterestFreeCalculator = ({
  annualFee,
  establishmentFee,
  minimumAmount,
  monthlyFee,
  monthlyTerms,
  importantInfo,
  noMinPurchaseText,
  superScriptText=''
}) => {
  const interestFreeOptions = monthlyTerms.map(term => ({
    label: `${term} Months`,
    value: term.toString()
  }));

  const paymentFrequencyOptions = [
    {
      label: 'Per month',
      value: '0',
      perYear: 12
    },
    {
      label: 'Per week',
      value: '1',
      perYear: 52
    },
    {
      label: 'Per day',
      value: '2',
      perYear: 365
    }
  ];

  const initialValues = {
    interestFreePeriod: interestFreeOptions[0].value,
    purchaseAmount: minimumAmount.toString(),
    paymentFrequency: paymentFrequencyOptions[0].value
  };

  return (
    <BasisProvider theme={defaultTheme}>
      <Form initialValues={initialValues}>
        {({ state }) => {
          // console.log(state);

          const interestFreePeriod = parseInt(
            state.values.interestFreePeriod,
            10
          );
          const purchaseAmount = parseFloat(state.values.purchaseAmount);
          const paymentFrequency =
            paymentFrequencyOptions[
              parseInt(state.values.paymentFrequency, 10)
            ];

          let result;
          let total;
          if (purchaseAmount < minimumAmount) {
            //   $('.error').text('Interest free is only available on amounts of $' + minimum_amount + ' or more').slideDown();
            //   return "$0.00";
            result = 0;
            total = 0;
          } else {
            result =
              (purchaseAmount / interestFreePeriod) *
                (12 / paymentFrequency.perYear) +
              monthlyFee * (12 / paymentFrequency.perYear);

            total =
              interestFreePeriod * monthlyFee +
              establishmentFee +
              purchaseAmount +
              annualFee * Math.round(interestFreePeriod / 12);
          }

          return (
            <div
              css={`
                > div {
                  &:nth-child(2),
                  &:last-child {
                    margin-top: 20px;
                    text-align: center;
                  }
                }

                @media (min-width: ${BREAKPOINT.XS}) {
                  display: flex;
                  align-items: center;

                  > div:first-child {
                    width: 42%;
                  }

                  > div:last-child {
                    width: 52%;

                    @media (min-width: ${BREAKPOINT.LG}) {
                      width: 45.5%;
                    }
                  }
                }
              `}
            >
              <div
                css={`
                  > div:not(:first-child) {
                    margin-top: 20px;
                  }
                `}
              >
                <Input
                  name="purchaseAmount"
                  label="Purchase amount"
                  type="number"
                  helpText={noMinPurchaseText ? '' :`Min purchase ${currency.format(minimumAmount)}`}
                />
                <div
                  css={`
                    @media (min-width: ${BREAKPOINT.LG}) {
                      display: flex;
                      justify-content: space-between;
                      > div {
                        width: 48%;
                      }
                    }
                  `}
                >
                  <Select
                    name="interestFreePeriod"
                    label="Interest free period"
                    options={interestFreeOptions}
                  />
                  <Select
                    name="paymentFrequency"
                    label="Payment frequency"
                    options={paymentFrequencyOptions}
                  />
                </div>
              </div>
              <FramedContainer
                theme={{
                  contentBorderColor: COLOR.BLUE_SKY,
                  frameColor: '#B1DBFF'
                }}
              >
                <div
                  css={`
                    background: ${COLOR.BLUE_SKY};
                    padding: 32px;

                    @media (min-width: ${BREAKPOINT.XS}) {
                      padding: 8px;
                    }

                    @media (min-width: ${BREAKPOINT.LG}) {
                      padding: 16px 32px;
                    }
                  `}
                >
                  <div>
                    <Text textStyle="body1">
                      <strong
                        css={`
                          @media (min-width: ${BREAKPOINT.LG}) {
                            font-size: 1.2em;
                          }
                        `}
                      >
                        Your Interest Free payment plan
                      </strong>
                    </Text>
                    <div
                      css={`
                        margin: 8px 0;
                      `}
                    >
                      <Text textStyle="body" color="primary.blue.t100">
                        from
                      </Text>
                      <Text textStyle="heading3" color="primary.blue.t100">
                        <span
                          css={`
                            @media (min-width: ${BREAKPOINT.LG}) {
                              font-size: 1.5em;
                            }
                            &:before {
                              content: '';
                              position: absolute;
                              top: 57px;
                              width: 34px;
                              height: 81px;
                              margin-left: -14px;
                              background-image: url(${require('./images/sizzles-mobile-left.svg')});
                              background-size: contain;

                              @media (min-width: ${BREAKPOINT.XS}) {
                                top: 35px;
                                margin-left: -14px;
                              }

                              @media (min-width: ${BREAKPOINT.LG}) {
                                top: 64px;
                                height: 60px;
                                margin-left: -38px;
                                background-image: url(${require('./images/sizzles-desktop-left.svg')});
                              }
                            }
                            &:after {
                              content: '';
                              position: absolute;
                              top: 72px;
                              width: 26px;
                              height: 60px;
                              margin-left: -14px;
                              background-image: url(${require('./images/sizzles-mobile-right.svg')});
                              background-size: contain;

                              @media (min-width: ${BREAKPOINT.XS}) {
                                top: 49px;
                                margin-left: -12px;
                              }

                              @media (min-width: ${BREAKPOINT.LG}) {
                                top: 65px;
                                width: 35px;
                                height: 52px;
                                margin-left: 4px;
                                background-image: url(${require('./images/sizzles-desktop-right.svg')});
                              }
                            }
                          `}
                        >
                          {currency.format(result)}
                        </span>
                      </Text>
                      <Text textStyle="body" color="primary.blue.t100">
                        {paymentFrequency.label.toLowerCase()}
                        <sup>*</sup>
                      </Text>
                    </div>

                    <Text textStyle="body2">
                      <sup>*</sup>
                      {annualFee > 0 &&
                        `Excludes $${annualFee} annual account fee. `}
                      {monthlyFee > 0 &&
                        ReactHTMLParser(
                          `Includes a monthly credit card fee (currently $${monthlyFee}, subject to change)<sup>${superScriptText}</sup>.`
                        )}
                      {paymentFrequency.label !== 'Per month' &&
                        'Monthly repayments required, exact amount specified in your statement.'}
                    </Text>
                  </div>
                  <hr
                    css={`
                      border-top: 2px solid ${COLOR.BLUE_LIGHT};
                    `}
                  />
                  <div>
                    <Text textStyle="subtitle2" color="primary.blue.t100">
                      <strong
                        css={`
                          font-weight: 400 !important;
                        `}
                      >
                        Total amount paid<sup>^</sup>
                      </strong>
                    </Text>
                    <Text textStyle="heading4" color="primary.blue.t100">
                      <span
                        css={`
                          @media (min-width: ${BREAKPOINT.LG}) {
                            font-size: 1.1em;
                          }
                        `}
                      >
                        {currency.format(total)}
                      </span>
                    </Text>
                    <Text textStyle="body2">
                      <span
                        css={`
                          margin-top: 8px;
                          display: block;
                        `}
                      >
                        <sup>^</sup>
                        {annualFee > 0 &&
                          `Includes a $${annualFee} annual account fee, charged after your first purchase
                          and each anniversary date. `}
                        {monthlyFee > 0 &&
                          ReactHTMLParser(`Includes a monthly credit card fee (currently $${monthlyFee}, subject to change)<sup>${superScriptText}</sup>.`)}
                        {establishmentFee > 0 &&
                          `Add $${establishmentFee} once only establishment fee for new customers.`}
                        {/* {totalDisclaimer} */}
                      </span>
                    </Text>
                  </div>
                </div>
              </FramedContainer>
            </div>
          );
        }}
      </Form>
      <div
        css={`
          margin-top: 20px;

          @media (min-width: ${BREAKPOINT.LG}) {
            margin-top: 40px;
          }
          > p {
            margin-bottom: 20px;
          }
        `}
      >
        <Text textStyle="heading4" align="center">
          Important things to remember
        </Text>

        {importantInfo ? (
          importantInfo.map(line => <Text>{ReactHTMLParser(line)}</Text>)
        ) : (
          <>
            <Text>
              These repayments are for Equal monthly payment plans only and do
              not take into account any other purchases or balances.
            </Text>

            {establishmentFee > 0 && (
              <Text>
                The instalment and total estimated price amounts exclude the
                one-off ${establishmentFee} establishment fee payable by new
                customers.
              </Text>
            )}

            <Text>
              The monthly, weekly and daily repayments are approximate only.
            </Text>

            <Text>
              Monthly repayments are required, exact amount specified in the
              customer’s statement.
            </Text>

            <Text>
              Minimum financed amount applies. See current promotions for terms
              and conditions.
            </Text>

            <Text>
              Various plans only available during specific promotional periods.
            </Text>
          </>
        )}
      </div>
    </BasisProvider>
  );
};
